import { Injectable, NgZone, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CrwdConfirmService, CrwdToastRef, CrwdToastService } from '@crewdle/ui-kit';

import { DraftService } from 'src/app/services/draft/draft.service';
import { PlatformService } from 'src/app/services/platform/platform.service';

/** What the mobile shell fires once a new web bundle is downloaded and queued (../mobile shell/src/web-update.ts). */
export const WEB_UPDATE_READY_EVENT = 'crewdle:webUpdateReady';

/** Mobile shell API v7 (`crewdleShell.webUpdate`). */
interface ShellWebUpdateApi {
  ready(): string | null;
  applyNow(): Promise<void>;
}

const shellWebUpdate = (): ShellWebUpdateApi | null =>
  (window as unknown as { crewdleShell?: { webUpdate?: ShellWebUpdateApi } }).crewdleShell?.webUpdate ?? null;

/**
 * Text the person typed and has not sent, anywhere on the page: a non-empty text field or editor.
 * Drafts kept for other conversations are in memory only, so they count too.
 */
export function hasUnsentInput(drafts: Pick<DraftService, 'hasDrafts'>, root: ParentNode = document): boolean {
  if (drafts.hasDrafts()) return true;
  const fields = root.querySelectorAll<HTMLElement>(
    'textarea, input:not([type]), input[type="text"], input[type="search"], input[type="email"], input[type="url"], [contenteditable="true"]',
  );
  return Array.from(fields).some((field) =>
    'value' in field ? !!(field as HTMLInputElement).value.trim() : !!field.textContent?.trim());
}

/**
 * The MOBILE app's web-bundle update, made safe (2026-10-20). The shell used to hot-reload the
 * WebView right after a download, on return to foreground, and that lost unsent text. Now it
 * queues the bundle for the next cold start and tells the page; this service turns that into
 * the kit's persistent info toast "Update ready · Reload" (the kit's `crwd-app-update` pill cannot be
 * used here: its action reloads the page after polling /version.json, while the shell must be told to
 * apply the bundle it already holds). Reload asks first when there is unsent text — the same posture
 * as the desktop shell: the person decides, nothing reloads under them.
 *
 * Inert everywhere else: web and desktop have their own update flows (AppVersionService,
 * the kit's version poll), and an older mobile shell (API < 7) has no `webUpdate`.
 */
@Injectable({ providedIn: 'root' })
export class ShellWebUpdateService {
  private readonly platform = inject(PlatformService);
  private readonly zone = inject(NgZone);
  private readonly drafts = inject(DraftService);
  private readonly confirms = inject(CrwdConfirmService);
  private readonly translate = inject(TranslateService);
  private readonly toasts = inject(CrwdToastService);

  private readonly readyVersion = signal<string | null>(null);
  private readonly applyingSignal = signal(false);
  /** The downloaded version waiting to be applied, or null. */
  readonly ready = this.readyVersion.asReadonly();

  private started = false;
  private toast: CrwdToastRef | null = null;

  start(): void {
    if (this.started || !this.platform.is('capacitor')) return;
    const api = shellWebUpdate();
    if (!api) return;
    this.started = true;
    this.readyVersion.set(api.ready());
    this.announce();
    window.addEventListener(WEB_UPDATE_READY_EVENT, (event) => {
      const version = (event as CustomEvent<{ version?: string }>).detail?.version ?? api.ready();
      this.zone.run(() => {
        this.readyVersion.set(version ?? null);
        this.announce();
      });
    });
  }

  /** Offer the downloaded update: one persistent toast with Reload. */
  private announce(): void {
    if (!this.readyVersion() || this.toast) return;
    this.toast = this.toasts.info(this.translate.instant('app_update.ready'), {
      duration: 0, // stays until acted on
      action: {
        label: this.translate.instant('app_update.reload'),
        callback: () => {
          // The kit dismisses the toast after the callback. If nothing reloaded (the person kept their
          // text, or the apply failed) it comes back, so there is always a way onto the new bundle.
          this.toast = null;
          void this.apply().then(() => {
            if (!this.applyingSignal()) this.announce();
          });
        },
      },
    });
  }

  /** Reload. Asks first if it would throw away unsent text. */
  async apply(): Promise<void> {
    const api = shellWebUpdate();
    if (!api || !this.readyVersion() || this.applyingSignal()) return;
    if (hasUnsentInput(this.drafts)) {
      const confirmed = await this.confirms.confirm({
        title: this.translate.instant('app_update.unsent-title'),
        message: this.translate.instant('app_update.unsent-message'),
        confirmLabel: this.translate.instant('app_update.reload'),
        cancelLabel: this.translate.instant('common.buttons.cancel'),
      });
      if (!confirmed) return;
    }
    this.applyingSignal.set(true);
    try {
      await api.applyNow(); // the WebView reloads into the new bundle
    } catch (error) {
      console.warn('[shell-web-update] applying the update failed; it applies on the next launch', error);
      this.applyingSignal.set(false);
    }
  }
}
